import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:89").replace(
  /\/$/,
  ""
);

const STORAGE_KEY = "omova_cart_items";
const DEFAULT_MINIMUM_ORDER_UNITS = 3;

const CartContext = createContext(null);

const sortObject = (value) => {
  if (Array.isArray(value)) {
    return value.map(sortObject);
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = sortObject(value[key]);
        return acc;
      }, {});
  }
  return value;
};

const buildCartKey = (type, payload) => {
  const sortedPayload = sortObject(payload);
  return `${type}:${JSON.stringify(sortedPayload)}`;
};

const normalizeQuantity = (value, fallback = DEFAULT_MINIMUM_ORDER_UNITS) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1, Math.trunc(parsed));
};

const normalizeUnitEquivalent = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1, Math.trunc(parsed));
};

const getDefaultUnitEquivalent = (item) => {
  if (!item || typeof item !== "object") return 1;

  if (item.type === "kit-omova") {
    return item.payload?.include_black_resin ? 3 : 2;
  }

  if (item.type === "kit-fibra-resina") {
    return 2;
  }

  if (item.type === "kit-fibra-resina-forrar" || item.type === "kit-fibra-resina-forrar-flakes") {
    return 3;
  }

  return 1;
};

const getItemTotalUnits = (item) => {
  const unitEquivalent = normalizeUnitEquivalent(
    item?.unitEquivalent,
    getDefaultUnitEquivalent(item)
  );
  return normalizeQuantity(item?.quantity, 1) * unitEquivalent;
};

const normalizeMinimumOrderUnits = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_MINIMUM_ORDER_UNITS;

  const minimumOrderUnits = Math.round(parsed);
  return minimumOrderUnits >= 1 ? minimumOrderUnits : DEFAULT_MINIMUM_ORDER_UNITS;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [shippingConfig, setShippingConfig] = useState({
    threshold: 499,
    cost: 199,
  });
  const [minimumOrderUnits, setMinimumOrderUnits] = useState(DEFAULT_MINIMUM_ORDER_UNITS);

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE_URL}/api/ecommerce/cart/config`)
      .then(async (response) => {
        const contentType = response.headers.get("content-type") || "";
        const text = await response.text();
        if (!response.ok || !contentType.includes("application/json")) {
          throw new Error("No se pudo cargar la configuración de envío.");
        }
        return JSON.parse(text);
      })
      .then((data) => {
        if (!active) return;
        const threshold = Number(data.shipping_threshold);
        const cost = Number(data.shipping_cost);
        setShippingConfig({
          threshold: Number.isFinite(threshold) ? threshold : 499,
          cost: Number.isFinite(cost) ? cost : 199,
        });
        setMinimumOrderUnits(normalizeMinimumOrderUnits(data.minimum_order_units));
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const key = buildCartKey(item.type, item.payload);
      const existing = prev.find((entry) => entry.key === key);
      const requestedQuantity = normalizeQuantity(item.quantity, 1);
      if (existing) {
        return prev.map((entry) =>
          entry.key === key
            ? { ...entry, quantity: entry.quantity + requestedQuantity }
            : entry
        );
      }
      return [
        ...prev,
        {
          ...item,
          key,
          unitEquivalent: normalizeUnitEquivalent(
            item.unitEquivalent,
            getDefaultUnitEquivalent(item)
          ),
          quantity: requestedQuantity,
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((key, nextQuantity) => {
    setItems((prev) =>
      prev.map((entry) =>
        entry.key === key
          ? { ...entry, quantity: normalizeQuantity(nextQuantity, 1) }
          : entry
      )
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((entry) => entry.key !== key));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((prev) => !prev), []);

  const subtotal = useMemo(
    () => items.reduce((acc, item) => acc + (item.unitPrice || 0) * item.quantity, 0),
    [items]
  );
  const totalUnits = useMemo(
    () => items.reduce((acc, item) => acc + getItemTotalUnits(item), 0),
    [items]
  );
  const isMinimumOrderMet = totalUnits >= minimumOrderUnits;
  const shipping =
    subtotal >= shippingConfig.threshold ? 0 : items.length ? shippingConfig.cost : 0;
  const total = subtotal + shipping;
  const iva = subtotal > 0 ? (subtotal * 0.16) / 1.16 : 0;

  const validateCart = useCallback(async () => {
    if (!items.length) return;
    if (totalUnits < minimumOrderUnits) {
      setValidationError(`El pedido mínimo es de ${minimumOrderUnits} unidades en total.`);
      return;
    }
    setIsValidating(true);
    setValidationError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/ecommerce/cart/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            type: item.type,
            quantity: item.quantity,
            payload: item.payload,
            name: item.name,
          })),
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      const text = await response.text();
      if (!contentType.includes("application/json")) {
        throw new Error("No se pudo validar el carrito.");
      }
      const data = JSON.parse(text);
      if (!response.ok) {
        throw new Error(data.message || "No se pudo validar el carrito.");
      }
      if (data.shipping_threshold !== undefined || data.shipping_cost !== undefined) {
        const threshold = Number(data.shipping_threshold);
        const cost = Number(data.shipping_cost);
        setShippingConfig((prev) => ({
          threshold: Number.isFinite(threshold) ? threshold : prev.threshold,
          cost: Number.isFinite(cost) ? cost : prev.cost,
        }));
      }
      if (data.minimum_order_units !== undefined) {
        setMinimumOrderUnits(normalizeMinimumOrderUnits(data.minimum_order_units));
      }

      setItems((prev) => {
        const next = [...prev];
        data.items.forEach((validated) => {
          const key = buildCartKey(validated.type, validated.payload);
          const index = next.findIndex((entry) => entry.key === key);
          if (index !== -1) {
            next[index] = {
              ...next[index],
              unitPrice: validated.unit_price,
              quantity: validated.quantity,
            };
          }
        });
        return next;
      });
    } catch (error) {
      setValidationError(error.message || "No se pudo validar el carrito.");
    } finally {
      setIsValidating(false);
    }
  }, [items, totalUnits, minimumOrderUnits]);

  const value = useMemo(
    () => ({
      items,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      isOpen,
      openCart,
      closeCart,
      toggleCart,
      subtotal,
      totalUnits,
      minimumOrderUnits,
      isMinimumOrderMet,
      shipping,
      total,
      iva,
      validateCart,
      isValidating,
      validationError,
      shippingConfig,
    }),
    [
      items,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      isOpen,
      openCart,
      closeCart,
      toggleCart,
      subtotal,
      totalUnits,
      minimumOrderUnits,
      isMinimumOrderMet,
      shipping,
      total,
      iva,
      validateCart,
      isValidating,
      validationError,
      shippingConfig,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};
